import offerConfig from "../config/memberPostSignupMembershipOffer.json";
export const MEMBER_POST_SIGNUP_MEMBERSHIP_OFFER_DEFAULT_SOURCE =
  "member_post_signup_membership_offer";
export const MEMBER_POST_SIGNUP_MEMBERSHIP_OFFER_ACTIVATED_AT =
  "2026-09-01T07:00:00.000Z";

const MEMBERSHIP_DISCOUNT_EXPERIMENT_PERCENTAGES = new Set([10, 20]);

const normalizeDiscountType = (type = "") => {
  const normalized = String(type || "")
    .trim()
    .toLowerCase();
  if (normalized === "dollar" || normalized === "dollars_off") return "dollars";
  if (normalized === "percent" || normalized === "percent_off")
    return "percent";
  return normalized;
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === "object" && typeof value.toDate === "function") {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime())
      ? parsed
      : null;
  }
  return null;
};

export function formatMembershipOfferChipLabel(config = offerConfig) {
  const discountType = normalizeDiscountType(config?.discount?.type);
  const value = Number(config?.discount?.value || 0);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (config?.labels?.chipLabel) return config.labels.chipLabel;
  if (discountType === "dollars") return `Save $${value.toFixed(0)}`;
  if (discountType === "percent") return `Save ${value}%`;
  return "";
}

export function formatMembershipOfferCheckoutLabel(config = offerConfig) {
  const discountType = normalizeDiscountType(config?.discount?.type);
  const value = Number(config?.discount?.value || 0);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (config?.labels?.checkoutDiscountLabel) {
    return config.labels.checkoutDiscountLabel;
  }
  if (discountType === "dollars") return `$${value.toFixed(0)} off`;
  if (discountType === "percent") return `${value}% off`;
  return "";
}

export function formatMembershipOfferSavingsValue(config = offerConfig) {
  const discountType = normalizeDiscountType(config?.discount?.type);
  const value = Number(config?.discount?.value || 0);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (discountType === "dollars") return `$${value.toFixed(0)}`;
  if (discountType === "percent") return `${value}%`;
  return "";
}

export function getMemberPostSignupMembershipOffer(config = offerConfig) {
  const discountType = normalizeDiscountType(config?.discount?.type);
  const value = Number(config?.discount?.value || 0);
  const enabled =
    config?.enabled !== false &&
    ["dollars", "percent"].includes(discountType) &&
    Number.isFinite(value) &&
    value > 0;

  return {
    id: config?.id || "member_post_signup_membership_offer",
    enabled,
    prepayEnabled: config?.prepayEnabled === true,
    promoKey: config?.promoKey || "member_post_signup_membership_offer",
    timingWindow: {
      kind: config?.timingWindow?.kind || "same_day",
      durationDays: Number(config?.timingWindow?.durationDays || 1),
      label: config?.timingWindow?.label || "today",
      expiresAtMode: config?.timingWindow?.expiresAtMode || "end_of_local_day",
    },
    discount: {
      type: discountType,
      value,
      currency: config?.discount?.currency || "USD",
    },
    labels: {
      introLabel:
        config?.labels?.introLabel || formatMembershipOfferChipLabel(config),
      pricingChipLabel:
        config?.labels?.pricingChipLabel ||
        config?.labels?.introLabel ||
        formatMembershipOfferChipLabel(config),
      chipLabel: formatMembershipOfferChipLabel(config),
      checkoutDiscountLabel: formatMembershipOfferCheckoutLabel(config),
      checklistSubtitle:
        config?.labels?.checklistSubtitle ||
        "Prepay to save; year starts later.",
      billingTitle: config?.labels?.billingTitle || "Membership",
      billingSubtitle:
        config?.labels?.billingSubtitle ||
        "Your year starts when you book your first visit.",
    },
  };
}

export function getMemberPostSignupMembershipOfferFromDiscount(
  discount = null,
  config = offerConfig,
) {
  const baseOffer = getMemberPostSignupMembershipOffer(config);
  const discountType = normalizeDiscountType(discount?.discountType);
  const value = Number(discount?.amount || 0);
  const isMatchingOffer =
    discount?.status === "active" &&
    discountType === "percent" &&
    MEMBERSHIP_DISCOUNT_EXPERIMENT_PERCENTAGES.has(value) &&
    (discount?.promoKey === baseOffer.promoKey ||
      discount?.programId === baseOffer.promoKey);

  if (!isMatchingOffer) return baseOffer;

  return getMemberPostSignupMembershipOfferForPercent(value, config, {
    offerId: discount?.offerId,
    currency: discount?.currency,
  });
}

export function getMemberPostSignupMembershipOfferForPercent(
  percent = 0,
  config = offerConfig,
  overrides = {},
) {
  const baseOffer = getMemberPostSignupMembershipOffer(config);
  const value = Number(percent || 0);
  if (!MEMBERSHIP_DISCOUNT_EXPERIMENT_PERCENTAGES.has(value)) {
    return baseOffer;
  }

  return getMemberPostSignupMembershipOffer({
    ...config,
    id: overrides?.offerId || `dashboard_checklist_same_day_${value}_percent`,
    discount: {
      ...config?.discount,
      type: "percent",
      value,
      currency: overrides?.currency || config?.discount?.currency || "USD",
    },
    labels: {
      ...config?.labels,
      introLabel: `Prepay today and save ${value}%`,
      pricingChipLabel: `Save ${value}% today`,
      chipLabel: `Save ${value}%`,
      checkoutDiscountLabel: `${value}% off today`,
      checklistSubtitle: "Prepay to save; year starts at booking.",
      billingTitle: "Membership",
      billingSubtitle: `Prepay today and save ${value}%. Your year starts when you book your first visit.`,
    },
  });
}

export function getMemberPostSignupMembershipPricingChipLabel({
  discount = null,
  percent = null,
  config = offerConfig,
} = {}) {
  const baseOffer = getMemberPostSignupMembershipOffer(config);
  const value = Number(percent ?? discount?.amount ?? 0);
  const discountType = discount?.discountType
    ? normalizeDiscountType(discount.discountType)
    : "percent";
  const isMatchingOffer =
    discountType === "percent" &&
    MEMBERSHIP_DISCOUNT_EXPERIMENT_PERCENTAGES.has(value) &&
    (discount?.promoKey === baseOffer.promoKey ||
      discount?.programId === baseOffer.promoKey);

  if (!isMatchingOffer) return "";

  return getMemberPostSignupMembershipOfferForPercent(value, config).labels
    .pricingChipLabel;
}

export function isMemberPostSignupMembershipOfferActive({
  memberCreatedAt = null,
  now = new Date(),
  config = offerConfig,
} = {}) {
  const offer = getMemberPostSignupMembershipOffer(config);
  if (!offer.enabled) return false;

  const createdDate = toDate(memberCreatedAt);
  const nowDate = toDate(now);
  if (!createdDate || !nowDate) return false;

  const elapsedMs = nowDate.getTime() - createdDate.getTime();
  const windowMs =
    Math.max(0, Number(offer.timingWindow.durationDays || 0)) *
    24 *
    60 *
    60 *
    1000;

  return elapsedMs >= 0 && windowMs > 0 && elapsedMs <= windowMs;
}

export function isMemberPostSignupMembershipOfferEligible(
  memberCreatedAt = null,
) {
  const createdDate = toDate(memberCreatedAt);
  return Boolean(
    createdDate &&
    createdDate.getTime() >=
      Date.parse(MEMBER_POST_SIGNUP_MEMBERSHIP_OFFER_ACTIVATED_AT),
  );
}

export function getMembershipPricingIntroCopy({
  memberCreatedAt = null,
  now = new Date(),
  config = offerConfig,
} = {}) {
  const offerActive = isMemberPostSignupMembershipOfferActive({
    memberCreatedAt,
    now,
    config,
  });
  const savings = formatMembershipOfferSavingsValue(config);
  const baseSubtitle =
    "Your year long membership starts when you book your first visit. Enjoy delightful help around the house.";

  return {
    title: offerActive ? "Prepay today" : "Become a member",
    subtitle:
      offerActive && savings
        ? `Prepay today to save ${savings}. ${baseSubtitle}`
        : baseSubtitle,
    offerActive,
  };
}

export function applyMemberPostSignupMembershipOffer({
  baseAmount = 0,
  config = offerConfig,
  memberCreatedAt = null,
  now = new Date(),
} = {}) {
  const offer = getMemberPostSignupMembershipOffer(config);
  const normalizedBaseAmount = Number(baseAmount || 0);
  const offerActive = memberCreatedAt
    ? isMemberPostSignupMembershipOfferActive({
        memberCreatedAt,
        now,
        config,
      })
    : false;
  if (
    !offer.enabled ||
    !offerActive ||
    !Number.isFinite(normalizedBaseAmount)
  ) {
    return {
      offer,
      baseAmount: normalizedBaseAmount,
      finalAmount: normalizedBaseAmount,
      hasDiscount: false,
    };
  }

  const finalAmount =
    offer.discount.type === "dollars"
      ? normalizedBaseAmount - offer.discount.value
      : normalizedBaseAmount * (1 - offer.discount.value / 100);

  const roundedFinalAmount = Math.max(0, Math.round(finalAmount * 100) / 100);

  return {
    offer,
    baseAmount: normalizedBaseAmount,
    finalAmount: roundedFinalAmount,
    hasDiscount: roundedFinalAmount !== normalizedBaseAmount,
  };
}

export function buildMembershipDiscountRecordFromOffer({
  memberId = "",
  now = null,
  memberCreatedAt = null,
  Timestamp = null,
  config = offerConfig,
  source = MEMBER_POST_SIGNUP_MEMBERSHIP_OFFER_DEFAULT_SOURCE,
  assignment = null,
  eligibilityAssignment = null,
} = {}) {
  const offer = getMemberPostSignupMembershipOffer(config);
  if (
    !offer.enabled ||
    !isMemberPostSignupMembershipOfferActive({
      memberCreatedAt: memberCreatedAt || now,
      now: now || new Date(),
      config,
    })
  ) {
    return null;
  }

  const createdDate = toDate(memberCreatedAt || now);
  const nowDate = toDate(now);
  const createdAt =
    Timestamp && now?.toJSDate
      ? Timestamp.fromDate(now.toJSDate())
      : Timestamp && now instanceof Date
        ? Timestamp.fromDate(now)
        : nowDate;
  const expiresAtDateTime =
    memberCreatedAt?.endOf &&
    offer.timingWindow.expiresAtMode === "end_of_local_day"
      ? memberCreatedAt.endOf("day")
      : memberCreatedAt?.plus && offer.timingWindow.durationDays > 0
        ? memberCreatedAt.plus({ days: offer.timingWindow.durationDays })
        : null;
  const expiresAtDate =
    !expiresAtDateTime &&
    createdDate instanceof Date &&
    offer.timingWindow.durationDays > 0
      ? new Date(
          createdDate.getTime() +
            offer.timingWindow.durationDays * 24 * 60 * 60 * 1000,
        )
      : null;
  const expiresAt =
    Timestamp && expiresAtDateTime?.toJSDate
      ? Timestamp.fromDate(expiresAtDateTime.toJSDate())
      : Timestamp && expiresAtDate
        ? Timestamp.fromDate(expiresAtDate)
        : expiresAtDateTime?.toJSDate
          ? expiresAtDateTime.toJSDate()
          : expiresAtDate;
  const experimentContext = assignment
    ? {
        experimentKey: assignment.experimentKey,
        revisionId: assignment.revisionId,
        variantKey: assignment.variantKey,
      }
    : null;

  return {
    id: `${offer.promoKey}_${memberId || "member"}`,
    status: "active",
    discountType: offer.discount.type,
    amount: offer.discount.value,
    currency: offer.discount.currency,
    createdAt,
    startsAt: createdAt,
    expiresAt,
    usedAt: null,
    revokedAt: null,
    promoKey: offer.promoKey,
    offerId: offer.id,
    programId: offer.promoKey,
    programRevision: offer.id,
    fundingSource: "company",
    memberStage: "account_unpaid",
    pricingArmPolicy: experimentContext
      ? "registered_discount_experiment"
      : "uniform_across_pricing_arms",
    ...(experimentContext ? { experimentContext } : {}),
    ...(eligibilityAssignment
      ? {
          eligibilityExperimentContext: {
            experimentKey: eligibilityAssignment.experimentKey,
            revisionId: eligibilityAssignment.revisionId,
            variantKey: eligibilityAssignment.variantKey,
          },
        }
      : {}),
    timingWindow: offer.timingWindow,
    appliedBy: source,
  };
}

export default getMemberPostSignupMembershipOffer;
